import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useApplicationNotifications, useApplicationTheme } from '@adc/ui-core'
import AuthToggleModal from '../../authToggle/authToggleModal'
import { hasPostSignUpSteps } from '../../authToggle/components'
import { AUTH_TYPES } from '../../authToggle/constants'
import {
  gtmAuthFailure,
  trackAuthModalToggle,
} from '../../../shared/services/tracking'
import { useApplicationContext } from '../context'
import { useApplicationQueues } from '../queues'

const AuthenticationModal = ({
  close,
  intent,
  authInitialActive = AUTH_TYPES.LOGIN,
  ...rest
}) => {
  const { featureFlags, loginOrSignup, setLoginOrSignup, capabilities } =
    useApplicationContext()

  const notifications = useApplicationNotifications()
  const theme = useApplicationTheme()

  useEffect(() => {
    if (authInitialActive && authInitialActive !== loginOrSignup) {
      setLoginOrSignup && setLoginOrSignup(authInitialActive)
    }
  }, [authInitialActive])

  const { postAuthentication, postOnboarding } = useApplicationQueues()

  const {
    onAuthenticationComplete,
    onOnboardingComplete,
    // WHY: this flag not only alters the target action of the submit button for the onboarding modal but also
    // the contents of the modal itself, which is why we´re using it instead of relying on the onBoardingComplete callback
    // which is why, after some discussion, we´re keeping it as is for now
    redirectToSearchOnSignup,
  } = capabilities.authentication.options

  const trackModalClose = () => {
    if (intent) {
      gtmAuthFailure({ intent, loginOrSignup })
    }
  }

  const onClose = () => {
    trackModalClose()
    postAuthentication.clear()
    postOnboarding.clear()
    close()
  }

  const onSuccess = (authType, user) => {
    if (authType === AUTH_TYPES.SIGNUP) {
      onSignupSuccess(user)
    } else {
      onLoginSuccess(user)
    }
  }

  const onLoginSuccess = async user => {
    await onAuthenticationComplete({ user, authType: AUTH_TYPES.LOGIN })
    onOnboardingComplete && onOnboardingComplete({ authType: AUTH_TYPES.LOGIN })
    postAuthentication.process(AUTH_TYPES.LOGIN, user)
    postOnboarding.process(AUTH_TYPES.LOGIN, user)
    close()
  }

  const onSignupSuccess = async user => {
    onAuthenticationComplete({ user, authType: AUTH_TYPES.SIGNUP })
    postAuthentication.process(AUTH_TYPES.SIGNUP, user)

    // if no more steps, just finish it
    if (!hasPostSignUpSteps(user.kind, theme)) {
      onSignupFlowFinish()
    }
  }

  const onSignupFlowFinish = () => {
    onOnboardingComplete &&
      onOnboardingComplete({ authType: AUTH_TYPES.SIGNUP })
    postOnboarding.process(AUTH_TYPES.SIGNUP)

    notifications.overlayBanner.open({
      role: 'info',
      title: 'Thank You for Signing up.',
    })
    close()
  }

  const trackAuthToggle = newAuthType => {
    trackAuthModalToggle(newAuthType)
    setLoginOrSignup(newAuthType)
  }

  return (
    <AuthToggleModal
      isOpen
      initialActive={authInitialActive}
      onSuccess={onSuccess}
      onClose={onClose}
      featureFlags={featureFlags}
      onSignupFlowFinish={onSignupFlowFinish}
      onAuthToggle={trackAuthToggle}
      authIntent={intent}
      redirectToSearch={redirectToSearchOnSignup}
      {...rest}
    />
  )
}

AuthenticationModal.propTypes = {
  intent: PropTypes.string,
  close: PropTypes.func.isRequired,
  authInitialActive: PropTypes.string.isRequired,
}

export default AuthenticationModal
